/**
 * ConfigPanel — Slide-in panel for editing a widget's config.
 *
 * Dynamically renders form fields from the widget's `configSchema` definition.
 * Supports all 10 WidgetConfigField types:
 *   text, number, boolean, select, multiselect,
 *   board_picker, column_picker, color, date_range, slider
 *
 * Changes are applied locally and saved on "Apply" click.
 */

import { useCallback, useEffect, useState, type ChangeEvent } from 'react';
import {
  Button,
  TextField,
  Checkbox,
  Dropdown,
  Slider,
  Label,
  IconButton,
} from 'monday-ui-react-core';
import { Close } from 'monday-ui-react-core/icons';
import { widgetRegistry } from './WidgetRegistry';
import type { WidgetConfigField, WidgetDefinition, MondayBoard } from '../core/types';
import { queryAllBoards } from '../core/api/queries';

// -------------------------------------------------------
// Individual field renderers
// -------------------------------------------------------

interface FieldProps {
  field: WidgetConfigField;
  value: unknown;
  onChange: (key: string, newValue: unknown) => void;
  boards?: MondayBoard[];
}

function TextField_({ field, value, onChange }: FieldProps) {
  return (
    <div className="config-field">
      <Label text={field.label} />
      <TextField
        id={field.key}
        value={String(value ?? field.defaultValue ?? '')}
        placeholder={field.placeholder}
        onChange={(v: string) => onChange(field.key, v)}
        size={TextField.sizes.SMALL}
      />
      {field.helpText && <p className="config-field-help">{field.helpText}</p>}
    </div>
  );
}

function NumberField({ field, value, onChange }: FieldProps) {
  return (
    <div className="config-field">
      <Label text={field.label} />
      <input
        id={field.key}
        type="number"
        className="config-field__number-input"
        value={String(value ?? field.defaultValue ?? 0)}
        onChange={(e) => onChange(field.key, Number(e.target.value))}
      />
      {field.helpText && <p className="config-field-help">{field.helpText}</p>}
    </div>
  );
}

function BooleanField({ field, value, onChange }: FieldProps) {
  return (
    <div className="config-field config-field--inline">
      <Checkbox
        label={field.label}
        checked={Boolean(value ?? field.defaultValue ?? false)}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          onChange(field.key, e.target.checked)
        }
      />
      {field.helpText && <p className="config-field-help">{field.helpText}</p>}
    </div>
  );
}

function SelectField({ field, value, onChange }: FieldProps) {
  const options = (field.options ?? []).map((o) => ({ value: o.value, label: o.label }));
  const selected = options.find((o) => o.value === value) ?? null;
  return (
    <div className="config-field">
      <Label text={field.label} />
      <Dropdown
        options={options}
        value={selected}
        onChange={(opt: { value: unknown } | null) => onChange(field.key, opt?.value ?? null)}
        size={Dropdown.size.SMALL}
        placeholder={field.placeholder ?? 'Select…'}
        clearable
      />
      {field.helpText && <p className="config-field-help">{field.helpText}</p>}
    </div>
  );
}

function MultiSelectField({ field, value, onChange }: FieldProps) {
  const options = (field.options ?? []).map((o) => ({ value: o.value, label: o.label }));
  const vals = Array.isArray(value) ? (value as unknown[]) : [];
  const selected = options.filter((o) => vals.includes(o.value));
  return (
    <div className="config-field">
      <Label text={field.label} />
      <Dropdown
        options={options}
        value={selected}
        onChange={(opts: Array<{ value: unknown }> | null) =>
          onChange(field.key, (opts ?? []).map((o) => o.value))
        }
        size={Dropdown.size.SMALL}
        multi
        placeholder={field.placeholder ?? 'Select…'}
        clearable
      />
      {field.helpText && <p className="config-field-help">{field.helpText}</p>}
    </div>
  );
}

function BoardPickerField({ field, value, onChange, boards = [] }: FieldProps) {
  const options = boards.map((b) => ({ value: b.id, label: b.name }));
  const selected = options.find((o) => o.value === value) ?? null;
  return (
    <div className="config-field">
      <Label text={field.label} />
      <Dropdown
        options={options}
        value={selected}
        onChange={(opt: { value: unknown } | null) => onChange(field.key, opt?.value ?? null)}
        size={Dropdown.size.SMALL}
        placeholder="Select board…"
        clearable
      />
      {field.helpText && <p className="config-field-help">{field.helpText}</p>}
    </div>
  );
}

function SliderField({ field, value, onChange }: FieldProps) {
  const min = field.min ?? 0;
  const max = field.max ?? 100;
  const step = field.step ?? 1;
  return (
    <div className="config-field">
      <Label text={`${field.label}: ${value ?? field.defaultValue ?? min}`} />
      <Slider
        min={min}
        max={max}
        step={step}
        value={Number(value ?? field.defaultValue ?? min)}
        onChange={(v: number | number[]) => onChange(field.key, Array.isArray(v) ? v[0] : v)}
      />
      {field.helpText && <p className="config-field-help">{field.helpText}</p>}
    </div>
  );
}

function ColorField({ field, value, onChange }: FieldProps) {
  return (
    <div className="config-field">
      <Label text={field.label} />
      <input
        type="color"
        className="config-field__color-input"
        value={String(value ?? field.defaultValue ?? '#0085FF')}
        onChange={(e) => onChange(field.key, e.target.value)}
      />
      {field.helpText && <p className="config-field-help">{field.helpText}</p>}
    </div>
  );
}

// -------------------------------------------------------
// Field dispatcher
// -------------------------------------------------------

function ConfigField({ field, value, onChange, boards }: FieldProps) {
  switch (field.type) {
    case 'text': return <TextField_ field={field} value={value} onChange={onChange} />;
    case 'number': return <NumberField field={field} value={value} onChange={onChange} />;
    case 'boolean': return <BooleanField field={field} value={value} onChange={onChange} />;
    case 'select': return <SelectField field={field} value={value} onChange={onChange} />;
    case 'multiselect': return <MultiSelectField field={field} value={value} onChange={onChange} />;
    case 'board_picker': return <BoardPickerField field={field} value={value} onChange={onChange} boards={boards} />;
    case 'column_picker':
      // Simplified: treat as text for now (full impl would load columns after board selection)
      return <TextField_ field={field} value={value} onChange={onChange} />;
    case 'slider': return <SliderField field={field} value={value} onChange={onChange} />;
    case 'color': return <ColorField field={field} value={value} onChange={onChange} />;
    case 'date_range':
      // Date range: store as { from: string, to: string }
      return <TextField_ field={field} value={value} onChange={onChange} />;
    default:
      return null;
  }
}

// -------------------------------------------------------
// ConfigPanel
// -------------------------------------------------------

export interface ConfigPanelProps {
  /** The instanceId of the widget being configured */
  instanceId: string | null;
  /** The widgetId to look up the schema */
  widgetId: string | null;
  /** Current stored config for this instance */
  currentConfig: Record<string, unknown>;
  /** Called when user clicks Apply */
  onApply: (instanceId: string, newConfig: Record<string, unknown>) => void;
  /** Called when user dismisses the panel */
  onClose: () => void;
}

export function ConfigPanel({
  instanceId,
  widgetId,
  currentConfig,
  onApply,
  onClose,
}: ConfigPanelProps) {
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [boards, setBoards] = useState<MondayBoard[]>([]);

  const definition: WidgetDefinition | undefined = widgetId
    ? widgetRegistry.getWidget(widgetId)
    : undefined;

  // Initialise draft from current config + defaults when widget changes
  useEffect(() => {
    if (!definition) return;
    setDraft({ ...definition.defaultConfig, ...currentConfig });
  }, [definition, currentConfig]);

  // Load boards if any field is a board_picker
  useEffect(() => {
    const needsBoards = definition?.configSchema.some((f) => f.type === 'board_picker');
    if (!needsBoards) return;
    queryAllBoards()
      .then(setBoards)
      .catch((err) => console.error('[ConfigPanel] Failed to load boards:', err));
  }, [definition]);

  const handleChange = useCallback((key: string, value: unknown) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleApply = useCallback(() => {
    if (!instanceId) return;
    onApply(instanceId, draft);
    onClose();
  }, [instanceId, draft, onApply, onClose]);

  const handleReset = useCallback(() => {
    if (!definition) return;
    setDraft({ ...definition.defaultConfig });
  }, [definition]);

  if (!instanceId || !definition) return null;

  return (
    <aside className="config-panel" role="complementary" aria-label="Widget configuration">
      <div className="config-panel__header">
        <h2 className="config-panel__title">{definition.displayName}</h2>
        <IconButton icon={Close} onClick={onClose} ariaLabel="Close configuration" size="small" />
      </div>

      <div className="config-panel__body">
        {definition.configSchema.length === 0 ? (
          <p className="config-panel__empty">This widget has no configuration options.</p>
        ) : (
          definition.configSchema.map((field) => (
            <ConfigField
              key={field.key}
              field={field}
              value={draft[field.key]}
              onChange={handleChange}
              boards={boards}
            />
          ))
        )}
      </div>

      <div className="config-panel__footer">
        <Button
          kind={Button.kinds.TERTIARY}
          size={Button.sizes.SMALL}
          onClick={handleReset}
        >
          Reset to defaults
        </Button>
        <Button
          kind={Button.kinds.PRIMARY}
          size={Button.sizes.SMALL}
          onClick={handleApply}
        >
          Apply
        </Button>
      </div>
    </aside>
  );
}
